import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Trash, UploadSimple, FileZip, GitBranch } from '@phosphor-icons/react';

const SavedSites = () => {
  const [sites, setSites] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [gitForm, setGitForm] = useState({ name: '', git_url: '' });
  const [savingGit, setSavingGit] = useState(false);

  const fetchSites = async () => {
    try {
      const res = await axios.get(`/api/saved-sites`, { withCredentials: true });
      setSites(res.data);
    } catch (e) {
      toast.error("Failed to fetch saved sites");
    }
  };

  useEffect(() => {
    fetchSites();
  }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`/api/saved-sites`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Site saved successfully!");
      setFile(null);
      document.getElementById('file-input').value = "";
      fetchSites();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleAddGit = async (e) => {
    e.preventDefault();
    if (!gitForm.git_url) return;
    setSavingGit(true);
    try {
      await axios.post(`/api/saved-sites/git`, gitForm, { withCredentials: true });
      toast.success("Git template saved!");
      setGitForm({ name: '', git_url: '' });
      fetchSites();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save Git template");
    } finally {
      setSavingGit(false);
    }
  };

  const removeSite = async (id) => {
    if (!window.confirm("Delete this saved site?")) return;
    try {
      await axios.delete(`/api/saved-sites/${id}`, { withCredentials: true });
      fetchSites();
      toast.success("Site removed");
    } catch (e) {
      toast.error("Failed to remove");
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-4xl font-black mb-2">SAVED SITES</h1>
          <p className="text-slate-400">Save a ZIP or a public GitHub repo once, deploy it multiple times.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl">
      <div className="bg-card border border-white/10 p-8">
        <h2 className="text-xl font-bold mb-6">SAVE NEW ZIP</h2>
        <form onSubmit={handleUpload} className="space-y-4">
          <div className="border border-white/10 border-dashed bg-[#0A0B0E] p-6 relative hover:bg-white/5 transition-colors text-center cursor-pointer">
            <input 
              id="file-input"
              type="file" 
              accept=".zip"
              onChange={(e) => setFile(e.target.files[0])}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              required
            />
            {file ? (
              <div>
                <FileZip size={32} className="mx-auto text-primary mb-2" />
                <p className="font-mono text-sm font-bold text-white truncate max-w-[300px] mx-auto">{file.name}</p>
                <p className="text-xs text-slate-500 mt-1">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
            ) : (
              <div className="text-slate-500">
                <UploadSimple size={32} className="mx-auto mb-2 opacity-50" />
                <p className="font-bold text-sm text-white">Click or Drag Zip</p>
              </div>
            )}
          </div>
          <button 
            type="submit" 
            disabled={uploading || !file}
            className="w-full bg-primary text-black font-bold py-3 uppercase tracking-wider hover:bg-[#00B8CC] transition-colors disabled:opacity-50"
          >
            {uploading ? 'SAVING...' : 'SAVE SITE'}
          </button>
        </form>
      </div>

      <div className="bg-card border border-[#00E5FF]/20 p-8" data-testid="save-git-template">
        <h2 className="text-xl font-bold mb-6 flex items-center gap-2"><GitBranch size={22} className="text-[#00E5FF]" /> SAVE GIT TEMPLATE</h2>
        <form onSubmit={handleAddGit} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Template Name</label>
            <input value={gitForm.name} onChange={(e) => setGitForm({ ...gitForm, name: e.target.value })}
              placeholder="e.g. Agency Landing" data-testid="git-name-input"
              className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-[#00E5FF] text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Public GitHub Repo URL</label>
            <input required value={gitForm.git_url} onChange={(e) => setGitForm({ ...gitForm, git_url: e.target.value.trim() })}
              placeholder="https://github.com/user/repo" data-testid="git-url-input"
              className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-[#00E5FF] font-mono text-sm" />
            <p className="text-xs text-slate-500 mt-1">The repo's root must contain your static site (index.html).</p>
          </div>
          <button type="submit" disabled={savingGit || !gitForm.git_url} data-testid="save-git-btn"
            className="w-full bg-[#00E5FF] text-black font-bold py-3 uppercase tracking-wider hover:bg-[#00B8CC] transition-colors disabled:opacity-50">
            {savingGit ? 'SAVING...' : 'SAVE GIT TEMPLATE'}
          </button>
        </form>
      </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sites.map(site => (
          <div key={site.id} className="border border-white/10 bg-card p-6 relative flex flex-col gap-4">
            <div className="flex justify-between items-start">
              <div className="flex flex-col">
                {site.type === 'git'
                  ? <GitBranch size={24} className="text-[#00E5FF] mb-2" />
                  : <FileZip size={24} className="text-primary mb-2" />}
                <h3 className="font-mono font-bold text-sm truncate max-w-[200px]" title={site.name}>{site.name}</h3>
              </div>
              <button onClick={() => removeSite(site.id)} className="text-slate-400 hover:text-red-500 transition-colors p-1"><Trash size={20} /></button>
            </div>
            <div className="space-y-1 text-xs text-slate-400 border-t border-white/10 pt-4 mt-auto">
              {site.type === 'git' ? (
                <p className="truncate" title={site.git_url}><span className="text-[#00E5FF] uppercase font-bold mr-1">Git</span>{site.git_url}</p>
              ) : (
                <p>Size: {((site.size_bytes || 0) / 1024 / 1024).toFixed(2)} MB</p>
              )}
              <p>Uploaded: {format(new Date(site.created_at), 'MMM dd, yyyy')}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SavedSites;